import { supabase } from './supabase';
import { VisitPhoto } from '../types';

const BUCKET = 'visit-photos';
const MAX_EDGE = 1600;
export const MAX_PHOTOS_PER_STAMP = 12;

// Shrink a phone photo to a web-friendly JPEG before upload
export async function compressImage(file: File): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b || file), 'image/jpeg', 0.82)
    );
  } catch {
    return file;
  }
}

// Upload one photo to storage and return its public URL, or null on failure
async function uploadToStorage(
  appUserId: string,
  lighthouseId: number,
  file: File
): Promise<string | null> {
  const blob = await compressImage(file);
  const path = `${appUserId}/${lighthouseId}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 7)}.jpg`;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: 'image/jpeg' });
  if (error || !data) {
    console.error('Photo upload failed:', error?.message);
    return null;
  }
  return supabase.storage.from(BUCKET).getPublicUrl(data.path).data.publicUrl;
}

// Upload several photos and attach them to a visit. Returns the new rows.
export async function addVisitPhotos(
  appUserId: string,
  lighthouseId: number,
  visitId: number,
  files: File[]
): Promise<VisitPhoto[]> {
  const added: VisitPhoto[] = [];
  for (const file of files) {
    const url = await uploadToStorage(appUserId, lighthouseId, file);
    if (!url) continue;
    const { data, error } = await supabase
      .from('visit_photos')
      .insert({ visit_id: visitId, url })
      .select('*')
      .single();
    if (!error && data) added.push(data as VisitPhoto);
  }
  return added;
}

// Remove a photo row and its file from storage
export async function deleteVisitPhoto(photo: VisitPhoto): Promise<boolean> {
  const { error } = await supabase.from('visit_photos').delete().eq('id', photo.id);
  if (error) return false;
  const marker = `/${BUCKET}/`;
  const idx = photo.url.indexOf(marker);
  if (idx !== -1) {
    const path = decodeURIComponent(photo.url.slice(idx + marker.length));
    await supabase.storage.from(BUCKET).remove([path]);
  }
  return true;
}
